import { Badge, Card } from "@design";
import { useQuery } from "@tanstack/react-query";
import { Wrench } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import type { SkillResponse } from "@/api";
import { getSkillOptions } from "@/api";
import { EntityDetailPage } from "@/components/build/EntityDetailPage";
import { Text } from "@/components/Text";
import { EditWithAiButton } from "@/features/build/withAi/EditWithAiButton";
import { SkillAccessTab } from "./SkillAccessTab";
import { SkillForm } from "./SkillForm";
import { useDeleteSkill } from "./useDeleteSkill";

/** Build > Skills > detail: the shared EntityDetailPage scaffold; Overview (form + required tools), Access (membership). */
export function SkillDetailPage() {
  const { t } = useTranslation(["build", "skills"]);
  const navigate = useNavigate();
  const { uuid = "" } = useParams();
  const skill = useQuery(getSkillOptions({ path: { skill_uuid: uuid } }));
  const { onDelete, dialog } = useDeleteSkill(() => navigate("/build/skills"));
  const data = skill.data?.data;

  return (
    <EntityDetailPage
      listPath="/build/skills"
      rootLabel={t("skills:title")}
      name={data?.name ?? ""}
      isPending={skill.isPending}
      isError={skill.isError}
      notFoundLabel={t("skills:detail.notFound")}
      tabs={[
        {
          value: "overview",
          label: t("skills:tabs.overview"),
          content: data && (
            <div className="flex flex-col gap-4">
              <SkillForm skill={data} />
              <RequiredTools skill={data} />
            </div>
          ),
        },
        { value: "access", label: t("skills:tabs.access"), content: data && <SkillAccessTab skill={data} /> },
      ]}
      canDelete={!(data?.isSystem ?? true) && (data?.canManage ?? false)}
      onDelete={() => data && onDelete(data)}
      deleteDialog={dialog}
      extraActions={
        data?.canEdit && !data.isSystem ? <EditWithAiButton entityType="skill" uuid={data.uuid} /> : undefined
      }
    />
  );
}

function RequiredTools({ skill }: Readonly<{ skill: SkillResponse }>) {
  const { t } = useTranslation("skills");
  const tools = skill.tools ?? [];
  return (
    <Card>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 text-content-secondary">
          <Wrench size={16} aria-hidden />
          <Text variant="body">{t("tools.title")}</Text>
        </div>
        {tools.length > 0 ? (
          <ul className="flex flex-col divide-y divide-border-subtle">
            {tools.map((tool) => (
              <li key={tool.toolUuid} className="flex items-center gap-3 py-2">
                <Text variant="body-sm" className="flex-1 truncate">
                  {tool.toolName}
                </Text>
                {tool.isRequired && (
                  <Badge variant="neutral" size="compact">
                    {t("tools.required")}
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <Text variant="body-sm" tone="tertiary">
            {t("tools.empty")}
          </Text>
        )}
      </div>
    </Card>
  );
}
